// src/app/pages/claims/steps/column-mapping/claim-data-column-selection.component.ts
import { Component, Input, OnInit, ViewChild } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';
import { Observable, scheduled, asyncScheduler } from 'rxjs';

import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';
import { EntityConstants } from '../../../../../shared/constants/entity.constants';

import { RenewalClaimBatchResult, RenewalClaimService, WorkbookService, ImportColumnMappingService, ImportColumnService, CreateColumnMappingInput, ColumnMapping } from '../../../../../../service/proxy';

@Component({
    selector: 'claim-data-column-selection',
    standalone: true,
    templateUrl: './claim-data-column-selection.component.html',
    styleUrls: ['./claim-data-column-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        RenewalClaimService,
        ImportColumnService,
        ImportColumnMappingService,
        WorkbookService,
    ]
})
export class ClaimDataColumnSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public isWorksheetSelected: boolean = false;
    public headerIndex: number = 0;
    public batch: RenewalClaimBatchResult = new RenewalClaimBatchResult();

    public columnMappings: any[] = [];
    public databaseColumns: any[] = [];
    public validationMessage: string = '';

    constructor(
        private route: ActivatedRoute,
        private renewalClaimService: RenewalClaimService,
        private workbookService: WorkbookService,
        private importColumnService: ImportColumnService,
        private toastService: ToastService,
        private errorService: ErrorService
    ) {}

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    async onActivate(): Promise<void> {
        this.route.paramMap
            .subscribe(async (params) => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    try {
                        const batch = await this.getBatchById();

                        await this.getHeaderIndex(this.batchId);
                        await this.loadDatabaseColumnHeaders();
                        await this.loadWorkbookColumnHeaders(batch);
                        await this.getImportColumnMapping();
                    }
                    catch (error) {
                        this.toastService.show('An error occurred while activating the component. Please try again.', 'Error');
                    }
                }
            }
        );
    }

    private getBatchById(): Promise<RenewalClaimBatchResult> {
        return new Promise((resolve, reject) => {
            this.renewalClaimService.getRenewalClaimBatch(this.batchId)
                .subscribe({
                    next: (result) => {
                        this.batch = result;

                        resolve(result);
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load batch data. Please try again.', 'Error');

                        reject(error);
                    }
                }
            );
        });
    }

    private getHeaderIndex(batchId: number): Promise<number> {
        return new Promise((resolve, reject) => {
            this.renewalClaimService.getRenewalClaimRangeMapping(batchId)
                .subscribe({
                    next: (result) => {
                        this.headerIndex = result?.headerIndex ?? 1;

                        resolve(this.headerIndex);
                    },
                    error: (error) => {
                        this.toastService.show('Failed to retrieve header row info.', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                }
            );
        });
    }

    private loadDatabaseColumnHeaders(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.importColumnService.getImportColumnsByEntityName(EntityConstants.RenewalClaim)
                .subscribe({
                    next: (columns) => {
                        this.databaseColumns = columns;

                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load database columns. Please try again.', 'Error');

                        reject(error);
                    }
                }
            );
        });
    }

    private loadWorkbookColumnHeaders(batch: RenewalClaimBatchResult): Promise<void> {
        return new Promise((resolve, reject) => {
            const filePath = `${batch.filePath}\\${batch.storedName}`;
            const sheetIndex = 0;

            this.workbookService.getColumnsBySheetIndex(filePath, sheetIndex, this.headerIndex)
                .subscribe({
                    next: (result) => {
                        this.columnMappings = result.headers.map((header: any, index: number) => ({
                            batchId: this.batchId,
                            columnIndex: index,
                            sourceColumnDescription: header.label || `Column ${index + 1}`,
                            destinationColumn: '',
                            formatString: '',
                            regex: '',
                            showAdvanced: false
                        }));

                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load headers from file. Please try again.', 'Error');

                        reject(error);
                    }
                }
            );
        });
    }

    private getImportColumnMapping(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.renewalClaimService.getRenewalClaimImportColumnMapping(this.batchId)
                .subscribe({
                    next: (result) => {
                        if (!result || !result.mappings || result.mappings.length === 0) {
                            this.mapColumnSelectionsAutomatically();
                        }
                        else {
                            this.mapColumnSelectionsFromDatabase(result.mappings);
                        }

                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load database columns. Please try again.', 'Error');

                        reject(error);
                    }
                }
            );
        });
    }

    private mapColumnSelectionsAutomatically(): void {
        this.columnMappings.forEach(m => {
            const matchingDatabaseColumn = this.databaseColumns.find(dbCol =>
                dbCol.description.replace(' ', '').toLowerCase().includes(m['sourceColumnDescription'].replace(' ', '').toLowerCase())
            );

            if (matchingDatabaseColumn) {
                m.destinationColumn = matchingDatabaseColumn.name;
            }
        });
    }

    private mapColumnSelectionsFromDatabase(items: ColumnMapping[]): void {
        items.forEach((item: any) => {
            const row = this.columnMappings.find((m: any) => m.columnIndex === item.columnIndex);

            if (row) {
                row.destinationColumn = item.mappedPropertyName ?? row.destinationColumn;
                row.formatString = item.formatString ?? row.formatString;
                row.regex = item.regex ?? row.regex;
            }
        });
    }

    isValid(): boolean {
        if (!this.databaseColumns?.length) {
            return true; // Don't validate until data is loaded
        }

        // Check for duplicate mappings
        const mappedFields = this.columnMappings
            .map(m => m.destinationColumn)
            .filter(Boolean);
        
        const duplicates = mappedFields.filter((field, index) => 
            mappedFields.indexOf(field) !== index
        );
        
        if (duplicates.length > 0) {
            const uniqueDuplicates = [...new Set(duplicates)];
            const fieldNames = uniqueDuplicates
                .map(fieldName => this.databaseColumns.find(col => col.name === fieldName)?.description || fieldName)
                .join(', ');
            this.validationMessage = `The following fields are mapped multiple times: ${fieldNames}. Each field can only be mapped once.`;
            return false;
        }

        const requiredColumns = this.databaseColumns.filter(c => c.required);
        const requiredNames = requiredColumns.map(c => c.name);

        const selected = new Set(
            this.columnMappings
            .map(m => (m.destinationColumn || '').trim())
            .filter(Boolean)
        );

        const unmappedRequired = requiredColumns.filter(
            col => !selected.has(col.name)
        );

        if (unmappedRequired.length > 0) {
            const fieldNames = unmappedRequired.map(col => col.description).join(', ');
            this.validationMessage = `Please map all required fields before proceeding. Missing: ${fieldNames}`;
            return false;
        }

        this.validationMessage = '';
        return requiredNames.every(name => selected.has(name));
    }

    onMappingChange(): void {
        // Re-validate whenever a dropdown changes
        this.isValid();
    }

    onNext(): Observable<boolean> {
        if (!this.batchId || this.columnMappings.length === 0) {
            this.toastService.show('No column mappings to save.');
            return scheduled([false], asyncScheduler);
        }

        const missing = this.getMissingRequired();

        if (missing.length) {
            this.toastService.show('Map all fields marked with * to continue.', 'error');
            return scheduled([false], asyncScheduler);
        }

        const payload = new CreateColumnMappingInput();

        payload.mappings = this.columnMappings.map(({ columnIndex, destinationColumn, formatString, regex }) => {
            const mapped = new ColumnMapping();

            mapped.columnIndex = columnIndex;
            mapped.mappedPropertyName = destinationColumn;
            mapped.formatString = formatString || undefined;
            mapped.regex = regex || undefined;

            return mapped;
        });

        return new Observable<boolean>(observer => {
            this.renewalClaimService.createRenewalClaimColumnMapping(this.batchId, payload)
                .subscribe({
                    next: () => {
                        this.toastService.show('Mappings saved successfully!', 'Success');

                        const mappingsChangedKey = `renewal_claim_mappings_changed_${this.batchId}`;
                        sessionStorage.setItem(mappingsChangedKey, 'true');

                        observer.next(true);
                        observer.complete();
                    },
                    error: (err) => {
                        this.toastService.show('Failed to save column mappings. Please try again.', 'error');

                        observer.next(false);
                        observer.complete();
                    }
                }
            );
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }

    toggleAdvanced(mapping: any, event: Event): void {
        event.preventDefault();
        mapping.showAdvanced = !mapping.showAdvanced;
    }

    private getMissingRequired(): string[] {
        const selected = new Set(
            this.columnMappings.map(m => (m.destinationColumn || '').trim()).filter(Boolean)
        );
        return (this.databaseColumns || [])
            .filter((c: any) => c.required && !selected.has(c.name))
            .map((c: any) => c.description || c.name);
    }
}
